import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Sparkles,
  Calendar as CalendarIcon,
  Send,
  Edit,
  Trash2,
  Clock,
  MessageSquare,
  Loader2,
  Save,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  History,
  Truck,
  RefreshCcw
} from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { key: 'discovered', label: 'Discovered' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'outreach_sent', label: 'Outreach Sent' },
  { key: 'replied', label: 'Replied' },
  { key: 'declined', label: 'Declined' },
  { key: 'meeting_scheduled', label: 'Meeting Scheduled' },
  { key: 'active_client', label: 'Active Client' },
];

const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [segments, setSegments] = useState([]);
  const [emails, setEmails] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [draftingEmail, setDraftingEmail] = useState(false);
  const [recalculatingDistance, setRecalculatingDistance] = useState(false);
  const [scoringLead, setScoringLead] = useState(false);
  const [draftingFollowup, setDraftingFollowup] = useState(false);
  const [creatingScheduleLink, setCreatingScheduleLink] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [newMeeting, setNewMeeting] = useState({
    title: '',
    description: '',
    start_time: new Date(),
    end_time: new Date(),
    location: ''
  });

  const fetchData = React.useCallback(async () => {
    try {
      const [leadRes, segmentsRes, emailsRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/leads/${leadId}`, { withCredentials: true }),
        axios.get(`${API}/segments`, { withCredentials: true }),
        axios.get(`${API}/emails?lead_id=${leadId}`, { withCredentials: true }),
        axios.get(`${API}/meetings?lead_id=${leadId}`, { withCredentials: true })
      ]);
      setLead(leadRes.data);
      setSegments(segmentsRes.data);
      setEmails(emailsRes.data);
      setMeetings(meetingsRes.data);
      setNotes(leadRes.data.notes || '');
      setEditData(leadRes.data);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/pipeline');
    } finally {
      setLoading(false);
    }
  }, [leadId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/leads/${leadId}/notes?notes=${encodeURIComponent(notes)}`, {}, { withCredentials: true });
      toast.success('Notes saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUpdateLead = async () => {
    try {
      await axios.put(`${API}/leads/${leadId}`, editData, { withCredentials: true });
      toast.success('Lead updated');
      setEditing(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleRecalculateDistance = async () => {
    // Persist any pending address edits first so the backend geocodes the new value.
    const hasAddressEdits = ['street_address', 'city', 'state', 'zip_code'].some(
      (k) => editData[k] !== undefined && editData[k] !== lead?.[k]
    );
    setRecalculatingDistance(true);
    try {
      if (hasAddressEdits) {
        await axios.put(`${API}/leads/${leadId}`, editData, { withCredentials: true });
      }
      const { data } = await axios.post(
        `${API}/leads/${leadId}/calculate-distance`,
        {},
        { withCredentials: true }
      );
      if (data?.distance_miles != null) {
        toast.success(`Distance recalculated: ${data.distance_miles} mi`);
      } else if (data?.latitude == null) {
        toast.error('Could not geocode this address — check spelling and try again');
      } else {
        toast.success('Geocoded, but no delivery zone matched');
      }
      setEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to recalculate distance');
    } finally {
      setRecalculatingDistance(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await axios.put(`${API}/leads/${leadId}/stage?stage=${newStage}`, {}, { withCredentials: true });
      toast.success('Stage updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleScoreLead = async () => {
    setScoringLead(true);
    try {
      await axios.post(`${API}/leads/${leadId}/score`, {}, { withCredentials: true });
      toast.success('Lead scored successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to score lead');
    } finally {
      setScoringLead(false);
    }
  };

  const handleDraftEmail = async (emailType = 'initial', includeScheduleLink = false) => {
    setDraftingEmail(true);
    try {
      const response = await axios.post(`${API}/emails/draft`, {
        lead_id: leadId,
        email_type: emailType,
        include_schedule_link: includeScheduleLink
      }, { withCredentials: true });
      toast.success(includeScheduleLink ? 'Email drafted with scheduling link' : 'Email drafted');
      setSelectedEmail(response.data);
      setEmailDialogOpen(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to draft email');
    } finally {
      setDraftingEmail(false);
    }
  };

  const handleDraftFollowup = async () => {
    setDraftingFollowup(true);
    try {
      const response = await axios.post(`${API}/leads/${leadId}/draft-followup`, {}, { withCredentials: true });
      toast.success('Follow-up drafted for review');
      setSelectedEmail(response.data);
      setEmailDialogOpen(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to draft follow-up');
    } finally {
      setDraftingFollowup(false);
    }
  };

  const handleCreateScheduleLink = async () => {
    setCreatingScheduleLink(true);
    try {
      const response = await axios.post(`${API}/leads/${leadId}/create-schedule-link`, {}, { withCredentials: true });
      const url = response.data.schedule_url;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Scheduling link copied to clipboard!');
      } catch {
        // Clipboard API failed — show the link instead
        toast.success(
          <div>
            <p className="font-medium">Scheduling link created!</p>
            <p className="text-xs mt-1 break-all select-all">{url}</p>
          </div>,
          { duration: 10000 }
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create scheduling link');
    } finally {
      setCreatingScheduleLink(false);
    }
  };

  const [resendingScheduleLink, setResendingScheduleLink] = useState(false);
  const handleResendScheduleLink = async () => {
    setResendingScheduleLink(true);
    try {
      const res = await axios.post(
        `${API}/leads/${leadId}/resend-schedule-link`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data?.message || 'Scheduling link sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send scheduling link');
    } finally {
      setResendingScheduleLink(false);
    }
  };

  const handleApproveEmail = async () => {
    if (!selectedEmail) return;
    try {
      await axios.put(`${API}/emails/${selectedEmail.email_id}`, {
        email_id: selectedEmail.email_id,
        approved: true,
        subject: selectedEmail.subject,
        body: selectedEmail.body
      }, { withCredentials: true });
      toast.success('Email approved');
      setEmailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve email');
    }
  };

  const handleSendEmail = async (emailId) => {
    try {
      await axios.post(`${API}/emails/${emailId}/send`, {}, { withCredentials: true });
      toast.success('Email sent');
      setEmailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleCreateMeeting = async () => {
    try {
      await axios.post(`${API}/meetings`, {
        lead_id: leadId,
        title: newMeeting.title,
        description: newMeeting.description,
        start_time: newMeeting.start_time.toISOString(),
        end_time: newMeeting.end_time.toISOString(),
        location: newMeeting.location
      }, { withCredentials: true });
      toast.success('Meeting scheduled');
      setMeetingDialogOpen(false);
      setNewMeeting({
        title: '',
        description: '',
        start_time: new Date(),
        end_time: new Date(),
        location: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule meeting');
    }
  };

  const handleDeleteLead = async () => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { withCredentials: true });
      toast.success('Lead deleted');
      navigate('/pipeline');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const getScoreClass = (score) => {
    if (!score) return 'bg-gray-100 text-gray-600';
    if (score >= 70) return 'score-high';
    if (score >= 40) return 'score-medium';
    return 'score-low';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest"></div>
      </div>
    );
  }

  if (!lead) {
    return <div>Lead not found</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="lead-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/pipeline')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Lead Header Card */}
          <Card className="bg-white border-stone-100">
            <CardHeader className="flex flex-row items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <CardTitle className="font-heading text-2xl">{lead.business_name}</CardTitle>
                  {lead.claude_score && (
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getScoreClass(lead.claude_score)}`}>
                        <Sparkles className="inline h-4 w-4 mr-1" />
                        Score: {lead.claude_score}
                      </span>
                      {lead.score_change && lead.score_change !== 0 && (
                        <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                          lead.score_change > 0 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {lead.score_change > 0 ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : (
                            <TrendingDown className="h-4 w-4" />
                          )}
                          {lead.score_change > 0 ? '+' : ''}{lead.score_change}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`stage-${lead.stage}`}>
                    {STAGES.find(s => s.key === lead.stage)?.label || lead.stage}
                  </Badge>
                  {lead.segment_name && (
                    <Badge variant="secondary">{lead.segment_name}</Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-lead-btn">
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeleteLead} className="text-destructive" data-testid="delete-lead-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Contact Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                {lead.contact_name && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{lead.contact_name}</span>
                  </div>
                )}
                {lead.email && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <a href={`mailto:${lead.email}`} className="text-forest hover:underline truncate">{lead.email}</a>
                  </div>
                )}
                {lead.phone && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <a href={`tel:${lead.phone}`} className="text-forest hover:underline truncate">{lead.phone}</a>
                  </div>
                )}
                {(lead.full_address || lead.street_address || lead.city) && (
                  <div className="flex items-start gap-2 text-sm min-w-0">
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="break-words" data-testid="lead-address">
                      {lead.full_address || [lead.street_address, lead.city, lead.state, lead.zip_code].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {lead.website && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-forest hover:underline flex items-center gap-1 min-w-0">
                      <span className="truncate">{lead.website}</span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  </div>
                )}
              </div>

              {/* Stage Selector */}
              <div className="pt-4 border-t border-stone-100">
                <Label className="mb-2 block">Pipeline Stage</Label>
                <Select value={lead.stage} onValueChange={handleStageChange}>
                  <SelectTrigger className="w-full sm:w-64" data-testid="select-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((stage) => (
                      <SelectItem key={stage.key} value={stage.key}>
                        {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Claude Analysis */}
          <Card className="bg-white border-stone-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-wheat" />
                AI Analysis
              </CardTitle>
              <Button
                onClick={handleScoreLead}
                disabled={scoringLead}
                variant="outline"
                size="sm"
                data-testid="score-lead-btn"
              >
                {scoringLead ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    {lead.claude_score ? 'Re-analyze' : 'Analyze Lead'}
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent>
              {lead.claude_rationale ? (
                <div className="prose prose-sm max-w-none">
                  <p className="text-muted-foreground whitespace-pre-wrap">{lead.claude_rationale}</p>
                  {lead.outreach_angle && (
                    <div className="mt-3 p-3 bg-forest/5 rounded-lg border border-forest/10">
                      <p className="text-sm font-medium text-forest">Suggested Outreach Angle:</p>
                      <p className="text-sm text-muted-foreground mt-1">{lead.outreach_angle}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Click "Analyze Lead" to get AI-powered scoring and insights.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Score History */}
          {lead.score_history && lead.score_history.length > 0 && (
            <Card className="bg-white border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <History className="h-5 w-5 text-muted-foreground" />
                  Score History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {lead.score_history.slice().reverse().map((entry, index) => (
                    <div 
                      key={`score-${entry.timestamp || index}`} 
                      className="flex items-start justify-between p-3 bg-stone-50 rounded-lg border border-stone-100"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`flex items-center gap-1 text-sm font-medium ${
                            entry.change > 0 ? 'text-green-600' : entry.change < 0 ? 'text-red-600' : 'text-muted-foreground'
                          }`}>
                            {entry.change > 0 ? (
                              <TrendingUp className="h-4 w-4" />
                            ) : entry.change < 0 ? (
                              <TrendingDown className="h-4 w-4" />
                            ) : null}
                            {entry.previous_score} → {entry.new_score}
                            <span className="ml-1">
                              ({entry.change > 0 ? '+' : ''}{entry.change})
                            </span>
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{entry.reason}</p>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </div>
                    </div>
                  ))}
                </div>
                {lead.last_rescored_at && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Last rescored: {new Date(lead.last_rescored_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card className="bg-white border-stone-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this lead..."
                rows={4}
                className="mb-3"
                data-testid="notes-textarea"
              />
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="bg-forest hover:bg-forest-dark text-white"
                data-testid="save-notes-btn"
              >
                {savingNotes ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Notes
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Interaction History */}
          {lead.interaction_history?.length > 0 && (
            <Card className="bg-white border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Activity History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {lead.interaction_history.map((interaction, index) => (
                    <div key={`interaction-${interaction.timestamp || index}`} className="flex items-start gap-3 p-3 rounded-lg bg-sage/50">
                      <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium capitalize">{interaction.type.replace('_', ' ')}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(interaction.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:w-80 space-y-6">
          {/* Actions */}
          <Card className="bg-white border-stone-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => handleDraftEmail('initial')}
                disabled={draftingEmail}
                className="w-full bg-wheat hover:bg-wheat-dark text-white"
                data-testid="draft-email-btn"
              >
                {draftingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Drafting...
                  </>
                ) : (
                  <>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Draft Email
                  </>
                )}
              </Button>

              <Button
                onClick={() => handleDraftEmail('initial', true)}
                disabled={draftingEmail}
                variant="outline"
                className="w-full border-forest/30 text-forest hover:bg-forest/5"
                data-testid="draft-email-with-schedule-btn"
              >
                {draftingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Drafting...
                  </>
                ) : (
                  <>
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Draft Email + Schedule Link
                  </>
                )}
              </Button>

              {/* Declined follow-up button */}
              {lead.stage === 'declined' && (
                <Button
                  onClick={handleDraftFollowup}
                  disabled={draftingFollowup}
                  variant="outline"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="draft-followup-btn"
                >
                  {draftingFollowup ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Drafting...</>
                  ) : (
                    <><MessageSquare className="mr-2 h-4 w-4" />Draft Check-in Follow-up</>
                  )}
                </Button>
              )}

              {/* Decline reason display */}
              {lead.decline_reason && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-xs font-medium text-red-800 mb-1">Decline Reason</p>
                  <p className="text-sm text-red-700">{lead.decline_reason}</p>
                </div>
              )}

              {/* Schedule delivery link */}
              <Button
                onClick={handleCreateScheduleLink}
                disabled={creatingScheduleLink}
                variant="outline"
                className="w-full"
                data-testid="create-schedule-link-btn"
              >
                {creatingScheduleLink ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
                ) : (
                  <><CalendarIcon className="mr-2 h-4 w-4" />Copy Schedule Link</>
                )}
              </Button>

              {/* Email schedule link directly to lead */}
              {lead.email && (
                <Button
                  onClick={handleResendScheduleLink}
                  disabled={resendingScheduleLink}
                  className="w-full bg-forest hover:bg-forest-dark text-white"
                  data-testid="resend-schedule-link-btn"
                >
                  {resendingScheduleLink ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
                  ) : (
                    <><Send className="mr-2 h-4 w-4" />Email Schedule Link to {lead.email}</>
                  )}
                </Button>
              )}

              <Dialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full" data-testid="schedule-meeting-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    Schedule Meeting
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Schedule Meeting</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={newMeeting.title}
                        onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                        placeholder="Meeting with lead"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newMeeting.description}
                        onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                        placeholder="Meeting notes..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={newMeeting.location}
                        onChange={(e) => setNewMeeting({ ...newMeeting, location: e.target.value })}
                        placeholder="Address or video link"
                      />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {format(newMeeting.start_time, 'PPP')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={newMeeting.start_time}
                            onSelect={(date) => {
                              if (date) {
                                const currentStart = newMeeting.start_time;
                                const newStart = new Date(date);
                                newStart.setHours(currentStart.getHours(), currentStart.getMinutes(), 0, 0);
                                const newEnd = new Date(date);
                                newEnd.setHours(currentStart.getHours() + 1, currentStart.getMinutes(), 0, 0);
                                setNewMeeting({ ...newMeeting, start_time: newStart, end_time: newEnd });
                              }
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Time</Label>
                        <Select
                          value={`${newMeeting.start_time.getHours().toString().padStart(2, '0')}:${newMeeting.start_time.getMinutes().toString().padStart(2, '0')}`}
                          onValueChange={(value) => {
                            const [hours, minutes] = value.split(':').map(Number);
                            const newStart = new Date(newMeeting.start_time);
                            newStart.setHours(hours, minutes, 0, 0);
                            const newEnd = new Date(newMeeting.start_time);
                            newEnd.setHours(hours + 1, minutes, 0, 0);
                            setNewMeeting({ ...newMeeting, start_time: newStart, end_time: newEnd });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 24 }, (_, h) => 
                              ['00', '30'].map(m => {
                                const timeStr = `${h.toString().padStart(2, '0')}:${m}`;
                                const displayStr = `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${m} ${h < 12 ? 'AM' : 'PM'}`;
                                return <SelectItem key={timeStr} value={timeStr}>{displayStr}</SelectItem>;
                              })
                            ).flat()}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Duration</Label>
                        <Select
                          value={String(Math.round((newMeeting.end_time - newMeeting.start_time) / (1000 * 60)))}
                          onValueChange={(value) => {
                            const durationMinutes = parseInt(value);
                            const newEnd = new Date(newMeeting.start_time.getTime() + durationMinutes * 60 * 1000);
                            setNewMeeting({ ...newMeeting, end_time: newEnd });
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 minutes</SelectItem>
                            <SelectItem value="60">1 hour</SelectItem>
                            <SelectItem value="90">1.5 hours</SelectItem>
                            <SelectItem value="120">2 hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setMeetingDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateMeeting} className="bg-forest hover:bg-forest-dark text-white">
                      Schedule
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Emails */}
          <Card className="bg-white border-stone-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Emails</CardTitle>
            </CardHeader>
            <CardContent>
              {emails.length > 0 ? (
                <div className="space-y-2">
                  {emails.map((email) => (
                    <div
                      key={email.email_id}
                      className="p-3 rounded-lg border border-stone-200 hover:bg-sage/50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedEmail(email);
                        setEmailDialogOpen(true);
                      }}
                    >
                      <p className="text-sm font-medium truncate">{email.subject}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={email.status === 'sent' ? 'default' : 'secondary'} className={email.status === 'sent' ? 'bg-green-600' : ''}>
                          {email.status}
                        </Badge>
                        {email.approved && <Badge variant="outline">Approved</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No emails yet</p>
              )}
            </CardContent>
          </Card>

          {/* Meetings */}
          <Card className="bg-white border-stone-100">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Meetings</CardTitle>
            </CardHeader>
            <CardContent>
              {meetings.length > 0 ? (
                <div className="space-y-2">
                  {meetings.map((meeting) => (
                    <div key={meeting.meeting_id} className="p-3 rounded-lg border border-stone-200">
                      <p className="text-sm font-medium">{meeting.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(meeting.start_time).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No meetings scheduled</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Draft</DialogTitle>
          </DialogHeader>
          {selectedEmail && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Subject</Label>
                <Input
                  value={selectedEmail.subject}
                  onChange={(e) => setSelectedEmail({ ...selectedEmail, subject: e.target.value })}
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  value={selectedEmail.body}
                  onChange={(e) => setSelectedEmail({ ...selectedEmail, body: e.target.value })}
                  rows={10}
                />
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={selectedEmail.status === 'sent' ? 'default' : 'secondary'}>
                  {selectedEmail.status}
                </Badge>
                {selectedEmail.approved && <Badge variant="outline">Approved</Badge>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Close</Button>
            {selectedEmail && !selectedEmail.approved && (
              <Button onClick={handleApproveEmail} className="bg-forest hover:bg-forest-dark text-white">
                Approve Email
              </Button>
            )}
            {selectedEmail && selectedEmail.approved && selectedEmail.status !== 'sent' && (
              <Button 
                onClick={() => handleSendEmail(selectedEmail.email_id)} 
                className="bg-wheat hover:bg-wheat-dark text-white"
              >
                <Send className="mr-2 h-4 w-4" />
                Send Email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-stone-100">
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-testid="lead-edit-scroll-body">
            <div>
              <Label>Business Name</Label>
              <Input
                value={editData.business_name || ''}
                onChange={(e) => setEditData({ ...editData, business_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input
                value={editData.contact_name || ''}
                onChange={(e) => setEditData({ ...editData, contact_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={editData.email || ''}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={editData.phone || ''}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Street Address</Label>
              <Input
                value={editData.street_address || ''}
                onChange={(e) => setEditData({ ...editData, street_address: e.target.value })}
                placeholder="123 Main St"
                data-testid="lead-edit-street"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>City</Label>
                <Input
                  value={editData.city || ''}
                  onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  placeholder="Dobbs Ferry"
                  data-testid="lead-edit-city"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={editData.state || ''}
                  onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                  placeholder="NY"
                  maxLength={2}
                  data-testid="lead-edit-state"
                />
              </div>
              <div>
                <Label>Zip</Label>
                <Input
                  value={editData.zip_code || ''}
                  onChange={(e) => setEditData({ ...editData, zip_code: e.target.value })}
                  placeholder="10522"
                  data-testid="lead-edit-zip"
                />
              </div>
            </div>
            <div className="flex items-center justify-between -mt-1 text-xs">
              <span className="text-muted-foreground">
                {lead?.distance_miles != null
                  ? `Current: ${lead.distance_miles} mi from bakery${lead.delivery_zone ? ` · ${lead.delivery_zone}` : ''}`
                  : 'No distance calculated yet'}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRecalculateDistance}
                disabled={recalculatingDistance}
                data-testid="recalculate-distance-btn"
              >
                {recalculatingDistance ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Recalculating…
                  </>
                ) : (
                  <>
                    <Truck className="mr-2 h-3.5 w-3.5" />
                    Save & Recalculate Distance
                  </>
                )}
              </Button>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Category</Label>
                {editData.segment_name && editData.category !== editData.segment_name && (
                  <button
                    type="button"
                    onClick={() => setEditData({ ...editData, category: editData.segment_name })}
                    className="text-xs text-forest hover:underline inline-flex items-center gap-1"
                    data-testid="lead-edit-sync-category"
                  >
                    <RefreshCcw className="h-3 w-3" />
                    Match Segment
                  </button>
                )}
              </div>
              <Input
                value={editData.category || ''}
                onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                placeholder="e.g. Cafe, Restaurant, Hotel"
                data-testid="lead-edit-category"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Raw type captured from discovery. Used by the Pipeline category filter alongside Segment.
              </p>
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={editData.website || ''}
                onChange={(e) => setEditData({ ...editData, website: e.target.value })}
              />
            </div>
            <div>
              <Label>Segment</Label>
              <Select
                value={editData.segment_id || ''}
                onValueChange={(value) => setEditData({ ...editData, segment_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select segment" />
                </SelectTrigger>
                <SelectContent>
                  {segments.map((segment) => (
                    <SelectItem key={segment.segment_id} value={segment.segment_id}>
                      {segment.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-stone-100 shrink-0 bg-white">
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button onClick={handleUpdateLead} className="bg-forest hover:bg-forest-dark text-white" data-testid="lead-edit-save">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetail;
